import { useState } from 'react';
import { Link } from 'react-router-dom';
import SEOMeta from '@/components/feature/SEOMeta';
import ArticleCard from '@/components/base/ArticleCard';
import NewsletterForm from '@/components/feature/NewsletterForm';
import { categories, allArticles, faqs, testimonials } from '@/mocks/data';

const featuredSlugs = [
  'nv-xing-xing-jian-kang-chang-jian-wu-qu',
  'xing-sheng-huo-gou-tong-ji-qiao',
  'xing-xin-li-jian-kang-zi-zun',
  'zhong-nian-fu-qi-qin-mi-guan-xi',
  'an-quan-xing-xing-wei-zhi-nan',
  'xing-fu-sheng-huo-xin-li-xue',
];

const featuredArticles = allArticles.filter((a) => featuredSlugs.includes(a.slug));

const orgJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '性欲社',
  url: 'https://sportsix.github.io',
  description: '性欲社是专注于两性健康、亲密关系与性福教育的权威平台。提供科学、专业、无禁忌的性健康知识。',
  foundingDate: '2024',
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer service',
    availableLanguage: ['Chinese'],
  },
};

const websiteJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: '性欲社',
  url: 'https://sportsix.github.io',
  potentialAction: {
    '@type': 'SearchAction',
    target: 'https://sportsix.github.io/search?q={search_term_string}',
    'query-input': 'required name=search_term_string',
  },
};

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((f) => ({
    '@type': 'Question',
    name: f.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: f.answer,
    },
  })),
};

export default function HomePage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  return (
    <>
      <SEOMeta
        title="性欲社 - 两性健康知识与亲密关系指南"
        description="性欲社是专注于两性健康、亲密关系与性福教育的权威平台。提供科学、专业、无禁忌的性健康知识，帮助成年人建立健康的亲密关系观念，提升生活品质与伴侣沟通能力。"
        keywords="性欲社,两性健康知识,亲密关系指南,性福教育,成人健康科普,伴侣沟通技巧,性心理健康"
        canonicalUrl="https://sportsix.github.io"
        ogImage="https://readdy.ai/api/search-image?query=Warm%20minimalist%20abstract%20art%20for%20sexual%20health%20education%20platform%2C%20soft%20coral%20and%20cream%20tones%2C%20gentle%20organic%20shapes%2C%20professional%20yet%20warm%20brand%20image&width=1200&height=630&seq=og-home&orientation=landscape"
      />

      <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(websiteJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[520px] md:h-[620px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20artistic%20abstract%20illustration%20with%20soft%20flowing%20organic%20shapes%20in%20coral%20terracotta%20cream%20and%20warm%20beige%20tones%2C%20minimalist%20elegant%20composition%2C%20gentle%20gradients%20suggesting%20warmth%20intimacy%20and%20healing%2C%20no%20text%2C%20clean%20modern%20aesthetic&width=1600&height=900&seq=hero-bg-main&orientation=landscape"
            alt="性欲社 - 两性健康知识平台"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/45"></div>
          <div className="relative z-10 w-full px-4 md:px-6 text-center">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-white leading-tight max-w-3xl mx-auto">
              科学、温暖、无禁忌的<br />两性健康知识平台
            </h1>
            <p className="mt-5 text-base md:text-lg text-white/80 max-w-2xl mx-auto leading-relaxed">
              用专业的知识消除困惑，用温暖的态度打破沉默。<br className="hidden sm:block" />
              每一个关于爱与身体的问题，都值得被认真回答。
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/category/xing-jian-kang-zhi-shi"
                className="px-8 py-3.5 text-sm font-medium bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                开始探索
              </Link>
              <Link
                to="/about"
                className="px-8 py-3.5 text-sm font-medium bg-white/15 text-white rounded-full hover:bg-white/25 backdrop-blur-sm transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                了解更多
              </Link>
            </div>
          </div>
        </section>

        {/* Categories Section */}
        <section className="w-full px-4 md:px-6 py-16 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                探索知识分类
              </h2>
              <p className="mt-3 text-sm md:text-base text-foreground-500 max-w-xl mx-auto">
                从生理健康到情感关系，从心理调适到沟通技巧，全方位覆盖你需要了解的两性知识
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6 text-center hover:border-primary-200/50 hover:bg-background-100 transition-all duration-300 cursor-pointer"
                >
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-500 group-hover:bg-primary-500 group-hover:text-background-50 transition-all duration-300">
                    <i className={`${cat.icon} text-xl`}></i>
                  </div>
                  <h3 className="mt-4 text-sm md:text-base font-heading font-semibold text-foreground-900">
                    {cat.name}
                  </h3>
                  <p className="mt-2 text-xs text-foreground-500 leading-relaxed line-clamp-2">
                    {cat.description}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Articles Section */}
        <section className="w-full px-4 md:px-6 py-16 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between mb-10 gap-3">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                  精选文章
                </h2>
                <p className="mt-2 text-sm text-foreground-500">
                  由专业医生和心理咨询师撰写的原创科普内容
                </p>
              </div>
              <Link
                to="/category/xing-jian-kang-zhi-shi"
                className="text-sm font-medium text-primary-500 hover:text-primary-600 transition-colors flex items-center gap-1 whitespace-nowrap cursor-pointer"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {featuredArticles.map((article) => {
                const cat = categories.find((c) => c.slug === article.category);
                return (
                  <ArticleCard
                    key={article.slug}
                    slug={article.slug}
                    title={article.title}
                    excerpt={article.excerpt}
                    image={article.image}
                    author={article.author}
                    date={article.date}
                    readTime={article.readTime}
                    category={article.category}
                    categoryName={cat?.name || ''}
                  />
                );
              })}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="w-full px-4 md:px-6 py-16 md:py-20 bg-background-50">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
              读者心声
            </h2>
            <p className="mt-3 text-sm text-foreground-500">
              听听他们怎么说
            </p>
            <div className="mt-10 relative">
              <div className="bg-background-100 border border-background-200/70 rounded-lg p-8 md:p-10 min-h-[200px] flex flex-col justify-center">
                <i className="ri-double-quotes-l text-3xl text-accent-400 mb-4"></i>
                <p className="text-base md:text-lg text-foreground-700 leading-relaxed">
                  {testimonials[currentTestimonial].content}
                </p>
                <div className="mt-6">
                  <p className="text-sm font-semibold text-foreground-900">
                    {testimonials[currentTestimonial].name}
                  </p>
                  <p className="text-xs text-foreground-400">
                    {testimonials[currentTestimonial].role}
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-center gap-3 mt-6">
                {testimonials.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentTestimonial(idx)}
                    className={`w-2.5 h-2.5 rounded-full transition-all cursor-pointer ${
                      idx === currentTestimonial
                        ? 'bg-primary-500 w-6'
                        : 'bg-background-300 hover:bg-background-400'
                    }`}
                    aria-label={`查看第${idx + 1}条评价`}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="w-full px-4 md:px-6 py-16 md:py-20 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                常见问题
              </h2>
              <p className="mt-3 text-sm text-foreground-500">
                你可能关心的问题，这里都有答案
              </p>
            </div>
            <div className="flex flex-col gap-3">
              {faqs.map((faq, idx) => (
                <div
                  key={idx}
                  className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    className="w-full px-5 py-4 flex items-center justify-between text-left cursor-pointer"
                  >
                    <span className="text-sm md:text-base font-medium text-foreground-800 pr-4">
                      {faq.question}
                    </span>
                    <i
                      className={`ri-arrow-down-s-line text-foreground-400 transition-transform duration-300 text-lg flex-shrink-0 ${
                        openFaq === idx ? 'rotate-180' : ''
                      }`}
                    ></i>
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-300 ${
                      openFaq === idx ? 'max-h-96' : 'max-h-0'
                    }`}
                  >
                    <p className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Newsletter Section */}
        <section className="w-full px-4 md:px-6 py-16 md:py-20 bg-background-50">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-500">
              <i className="ri-mail-send-line text-xl"></i>
            </div>
            <h2 className="mt-5 text-2xl md:text-3xl font-heading font-bold text-foreground-900">
              订阅我们的Newsletter
            </h2>
            <p className="mt-3 text-sm text-foreground-500 max-w-md mx-auto">
              每周精选两性健康知识，直接发送到你的邮箱。无广告、无垃圾，可随时退订。
            </p>
            <div className="mt-6 flex justify-center">
              <NewsletterForm />
            </div>
          </div>
        </section>
      </main>
    </>
  );
}